/**
 * QuoteRequestModal — Opens from product detail "Request Quote" button.
 * Creates a quote order via POST /api/customer/request-quote
 */
import React, { useState } from 'react';
import { X, Loader2, Check, Send, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { PhoneInput } from '../components/PhoneInput';
import { isValidEmail, isValidPhoneDigits } from '../lib/validation';
import { toast } from 'sonner';
import { requestQuote } from '../api/customerApi';

export default function QuoteRequestModal({ product, language, onClose, supplierTenantId = 'quattro-stand' }) {
  const tr = language === 'tr';
  const [qty, setQty] = useState(1);
  const [brief, setBrief] = useState('');
  const [notes, setNotes] = useState('');
  const [company, setCompany] = useState({ company_name: '', contact_name: '', email: '', phone_code: '+90', phone_number: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const emailValid = !company.email || isValidEmail(company.email);
  const phoneValid = !company.phone_number || isValidPhoneDigits(company.phone_number, { min: 7, max: 15 });

  const handleSubmit = async () => {
    if (!brief.trim()) {
      toast.error(tr ? 'Kısa brifing zorunludur.' : 'Brief is required.');
      return;
    }
    if (company.email && !emailValid) {
      setEmailTouched(true);
      toast.error(tr ? 'Geçerli bir e-posta giriniz.' : 'Please enter a valid email.');
      return;
    }
    if (company.phone_number && !phoneValid) {
      setPhoneTouched(true);
      toast.error(tr ? 'Telefon 7-15 rakam içermelidir.' : 'Phone must be 7-15 digits.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await requestQuote({
        product_id:         product.id,
        product_name:       product.name,
        product_image:      product.images?.[0] || null,
        unit_price:         product.price,
        currency:           'USD',
        quantity:           qty,
        supplier_tenant_id: supplierTenantId,
        customer_company:   {
          company_name: company.company_name,
          contact_name: company.contact_name,
          email:        company.email,
          phone:        company.phone_number ? `${company.phone_code} ${company.phone_number}` : '',
        },
        brief,
        notes,
      });
      setDone(true);
      toast.success(tr ? `Teklif talebiniz iletildi. Sipariş No: ${result.order_number}` : `Quote submitted. Order: ${result.order_number}`);
      setTimeout(onClose, 2500);
    } catch (e) {
      toast.error(e.response?.data?.detail || (tr ? 'Hata oluştu.' : 'An error occurred.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="font-bold text-gray-900">{tr ? 'Teklif Talebi' : 'Request Quote'}</h2>
            <p className="text-sm text-gray-500 truncate max-w-xs">{product.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 transition-colors rounded-full p-1 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        {done ? (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <div className="h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center">
              <Check className="h-8 w-8 text-emerald-600" />
            </div>
            <p className="text-lg font-semibold text-gray-800">{tr ? 'Talebiniz iletildi!' : 'Quote request submitted!'}</p>
            <p className="text-sm text-gray-400">{tr ? 'Tedarikçi en kısa sürede dönecek.' : 'Supplier will respond soon.'}</p>
          </div>
        ) : (
          <div className="p-6 space-y-5 overflow-y-auto max-h-[70vh]">
            {/* Quantity */}
            <div className="flex items-center gap-4 bg-gray-50 rounded-2xl p-4">
              <div className="flex-1">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-0.5">{tr ? 'Ürün' : 'Product'}</p>
                <p className="font-semibold text-gray-900 text-sm">{product.name}</p>
                <p className="text-xs text-gray-400">${product.price} / {tr ? 'birim' : 'unit'}</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setQty(q => Math.max(1, q - 1))}
                  className="h-8 w-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-100">−</button>
                <span className="text-lg font-bold w-10 text-center">{qty}</span>
                <button onClick={() => setQty(q => q + 1)}
                  className="h-8 w-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-100">+</button>
              </div>
            </div>

            {/* Brief */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-500 uppercase">
                {tr ? 'Brifing / Gereksinimler' : 'Brief / Requirements'} <span className="text-red-500">*</span>
              </Label>
              <textarea
                rows={4}
                value={brief}
                onChange={e => setBrief(e.target.value)}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300 resize-none"
                placeholder={tr ? 'Ürünle ilgili detaylı gereksinimlerinizi yazın...' : 'Describe your requirements in detail...'}
              />
            </div>

            {/* Notes */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-500 uppercase">{tr ? 'Ek Notlar' : 'Additional Notes'}</Label>
              <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300 resize-none"
                placeholder={tr ? 'Teslimat tarihi, özel şartlar...' : 'Delivery date, special conditions...'} />
            </div>

            {/* Company info */}
            <div className="border border-gray-100 rounded-2xl p-4 space-y-3">
              <p className="text-xs font-semibold text-gray-500 uppercase">{tr ? 'İletişim Bilgileri' : 'Contact Info'}</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-gray-400">{tr ? 'Şirket Adı' : 'Company'}</Label>
                  <Input value={company.company_name} onChange={e => setCompany(c => ({ ...c, company_name: e.target.value }))}
                    className="rounded-xl text-sm h-9" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-400">{tr ? 'Yetkili' : 'Contact Name'}</Label>
                  <Input value={company.contact_name} onChange={e => setCompany(c => ({ ...c, contact_name: e.target.value }))}
                    className="rounded-xl text-sm h-9" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-400">E-posta</Label>
                  <Input
                    type="email"
                    value={company.email}
                    onChange={e => setCompany(c => ({ ...c, email: e.target.value.trim() }))}
                    onBlur={() => setEmailTouched(true)}
                    className={`rounded-xl text-sm h-9 ${emailTouched && company.email && !emailValid ? 'border-red-400' : ''}`}
                    autoComplete="email"
                  />
                  {emailTouched && company.email && !emailValid && (
                    <p className="text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {tr ? 'Geçerli bir e-posta giriniz' : 'Please enter a valid email'}
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-400">{tr ? 'Telefon' : 'Phone'}</Label>
                  <PhoneInput
                    codeValue={company.phone_code}
                    numberValue={company.phone_number}
                    onCodeChange={v => setCompany(c => ({ ...c, phone_code: v }))}
                    onNumberChange={v => { setCompany(c => ({ ...c, phone_number: v })); if (!phoneTouched && v) setPhoneTouched(true); }}
                  />
                  {phoneTouched && company.phone_number && !phoneValid && (
                    <p className="text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {tr ? 'Telefon 7–15 rakam olmalı' : 'Phone must be 7–15 digits'}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Submit */}
            <Button
              data-testid="quote-submit-btn"
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-12 gap-2">
              {submitting
                ? <><Loader2 className="h-4 w-4 animate-spin" />{tr ? 'Gönderiliyor...' : 'Submitting...'}</>
                : <><Send className="h-4 w-4" />{tr ? 'Teklif Talebi Gönder' : 'Send Quote Request'}</>}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
